/**
 * EmailOrPhoneInput — smart hybrid for any "Email or mobile" identifier
 * field (login, OTP send, forgot password).
 *
 * Behaviour:
 *   • Renders a single text input by default.
 *   • The moment the user starts typing a digit OR a "+" sign, the input
 *     transparently swaps to the full PhoneInput with a country-flag
 *     selector defaulting to India (+91). The value carries over so the
 *     user keeps typing without interruption.
 *   • The moment the user clears the field and re-types a letter, it
 *     swaps back to the plain text input (email mode).
 *
 * This way:
 *   • Email users see a normal email field.
 *   • Mobile users automatically get the country-code selector — no
 *     "enter +91 manually" friction.
 *   • Always stores the value in E.164 (+CCNNNNNN…) when in phone mode,
 *     plain text when in email mode. The parent component doesn't need
 *     to care.
 */
import { useEffect, useRef, useState } from "react";
import PhoneInput from "./PhoneInput";
import { findByCode } from "@/lib/countries";

const looksLikePhoneStart = (v) => {
  if (!v) return false;
  const trimmed = v.trim();
  return trimmed.startsWith("+") || /^\d/.test(trimmed);
};

export default function EmailOrPhoneInput({
  value = "",
  onChange,
  placeholder = "Email or mobile",
  testId = "email-or-phone-input",
  autoFocus = false,
  disabled = false,
  className = "",
  defaultCountry = "IN",
}) {
  // Mode is inferred from the current value — keeps the component
  // stateless from the parent's perspective. The parent owns the value.
  const [mode, setMode] = useState(() =>
    looksLikePhoneStart(value) ? "phone" : "text"
  );
  // When the user types a digit in the email field we transparently swap
  // to PhoneInput. Carry focus across the swap so they can keep typing
  // without an extra click.
  const swappedToPhoneRef = useRef(false);

  // Re-sync mode whenever the value changes from outside (e.g. demo
  // chip pre-fills the form, or a forgot-password flow resets it).
  useEffect(() => {
    const wantPhone = looksLikePhoneStart(value);
    if (wantPhone && mode !== "phone") setMode("phone");
    if (!wantPhone && value === "" && mode === "phone") setMode("text");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Reset the swap-focus flag once we've handed it to PhoneInput so a
  // later external value-change doesn't steal focus from the page.
  useEffect(() => {
    if (mode === "phone" && swappedToPhoneRef.current) {
      const t = setTimeout(() => { swappedToPhoneRef.current = false; }, 50);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [mode]);

  if (mode === "phone") {
    return (
      <PhoneInput
        value={value}
        onChange={onChange}
        placeholder="Enter mobile number"
        defaultCountry={defaultCountry}
        autoFocus={autoFocus || swappedToPhoneRef.current}
        disabled={disabled}
        testId={testId}
        className={className}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        // The very first digit/+ that comes in flips us to phone mode.
        // If the user typed a bare digit (no "+"), prefix the default
        // country's dial code BEFORE we hand the value to PhoneInput —
        // otherwise its splitE164 would interpret the lone digit as a
        // country dial code (e.g. "7" → Russia/Kazakhstan, "1" → US/CA)
        // and silently drop the digit from the national number.
        if (looksLikePhoneStart(next)) {
          swappedToPhoneRef.current = true;
          let normalized = next.trim();
          if (!normalized.startsWith("+")) {
            const dial = findByCode(defaultCountry)?.dial || "91";
            normalized = `+${dial}${normalized.replace(/\D/g, "")}`;
          }
          onChange?.(normalized);
          setMode("phone");
        } else {
          onChange?.(next);
        }
      }}
      placeholder={placeholder}
      autoFocus={autoFocus}
      disabled={disabled}
      data-testid={testId}
      className={`w-full h-12 px-4 rounded-xl border border-border bg-white focus:outline-none focus:ring-2 focus:ring-terracotta ${className}`}
      inputMode="text"
      autoComplete="username"
    />
  );
}
